import React from "react";

import { Box, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Tooltip, Typography, CircularProgress } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarIcon from "@mui/icons-material/Star";

import { useLanguage } from "../../config/language/language";
import { queryMenu } from "../../helper-functions/api/menuQueries";
import { isFavorite, resolveFavorites, toggleFavorite as toggleFavoriteEntry } from "../../helper-functions/shared/favorites";
import MenuAccessMessage from "../../helper-components/misc/MenuAccessMessage";
import CocktailImage from "../menu/components/CocktailImage";

export default function FavoritesPage(props) {
    const LANG = useLanguage();
    const menuData = props.menu.menuData;
    const favorites = props.favorites?.favorites || [];
    const [selected, setSelected] = React.useState(null);

    React.useEffect(() => {
        if (!menuData && props.server.isConnectionToServerEstablished) {
            queryMenu(props);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.server.isConnectionToServerEstablished, menuData, props.prefs?.guestPrefs?.venue]);

    // Migrate legacy id keys (recipe-N / curated-N) → cocktail names once menu is loaded
    React.useEffect(() => {
        if (!menuData || !props.favorites?.setFavorites) return;
        const { favorites: next } = resolveFavorites(favorites, menuData);
        const same = next.length === favorites.length && next.every((name, i) => name === favorites[i]);
        if (!same) {
            props.favorites.setFavorites(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuData]);

    if (!menuData) {
        const menuError = props.menu.menuError;
        const requiresLogin = Boolean(props.auth?.authStatus?.requiresLogin);
        const isLoggedIn = Boolean(props.user?.isLoggedIn);
        const showAccessGate = menuError || (requiresLogin && !isLoggedIn);

        if (showAccessGate) {
            return (
                <MenuAccessMessage
                    error={menuError}
                    requiresLogin={requiresLogin}
                    isLoggedIn={isLoggedIn}
                    onRetry={() => {
                        props.menu.setMenuError?.(null);
                        queryMenu(props);
                    }}
                    onSignIn={() => props.browser.navigate("/login")}
                />
            );
        }

        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const { drinks } = resolveFavorites(favorites, menuData);

    const toggleFavorite = (drink, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        const wasFavorite = isFavorite(favorites, drink);
        props.favorites.setFavorites(toggleFavoriteEntry(favorites, drink));
        if (wasFavorite && selected?.name === drink.name) {
            setSelected(null);
        }
    };

    const askBartenderAbout = (drink, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        setSelected(null);
        props.browser.navigate("/chat", {
            drinkName: drink.name,
            drinkAskId: Date.now()
        });
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h4" gutterBottom>
                {LANG.FAVORITES_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {LANG.FAVORITES_SUBTITLE}
            </Typography>

            {drinks.length === 0 ? (
                <Typography color="text.secondary">{LANG.FAVORITES_EMPTY}</Typography>
            ) : (
                <Grid container spacing={2} sx={{ width: "100%" }}>
                    {drinks.map((drink) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={drink.name}>
                            <Card sx={{ position: "relative", height: "100%" }}>
                                <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                                    <Tooltip title={LANG.MENU_UNFAVORITE}>
                                        <IconButton
                                            size="small"
                                            aria-label={LANG.MENU_UNFAVORITE}
                                            onClick={(e) => toggleFavorite(drink, e)}
                                            sx={{
                                                bgcolor: "background.paper",
                                                boxShadow: 1,
                                                "&:hover": { bgcolor: "background.paper" }
                                            }}>
                                            <StarIcon fontSize="small" color="warning" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={LANG.MENU_ASK_BARTENDER}>
                                        <IconButton
                                            size="small"
                                            aria-label={LANG.MENU_ASK_BARTENDER}
                                            onClick={(e) => askBartenderAbout(drink, e)}
                                            sx={{
                                                bgcolor: "background.paper",
                                                boxShadow: 1,
                                                "&:hover": { bgcolor: "background.paper" }
                                            }}>
                                            <AutoAwesomeIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Box
                                    onClick={() => setSelected(drink)}
                                    sx={{ height: "100%", cursor: "pointer" }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setSelected(drink);
                                        }
                                    }}>
                                    <CocktailImage drink={drink} height={120} />
                                    <CardContent>
                                        <Typography variant="h6" noWrap>
                                            {drink.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {drink.baseIngredient}
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                            {drink.readyToMake && <Chip size="small" color="success" label={LANG.MENU_CHIP_READY} />}
                                            {drink.onCuratedMenu && <Chip size="small" label={LANG.MENU_CHIP_ON_MENU} />}
                                        </Stack>
                                    </CardContent>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
                {selected && (
                    <>
                        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pr: 1 }}>
                            <Typography component="span" variant="h6">
                                {selected.name}
                            </Typography>
                            <Stack direction="row">
                                <Tooltip title={LANG.MENU_UNFAVORITE}>
                                    <IconButton aria-label={LANG.MENU_UNFAVORITE} onClick={(e) => toggleFavorite(selected, e)} color="warning">
                                        <StarIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={LANG.MENU_ASK_BARTENDER}>
                                    <IconButton aria-label={LANG.MENU_ASK_BARTENDER} onClick={(e) => askBartenderAbout(selected, e)} color="primary">
                                        <AutoAwesomeIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box sx={{ mb: 2 }}>
                                <CocktailImage drink={selected} height={230} borderRadius={1} />
                            </Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {selected.baseIngredient}
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                                {selected.ingredients}
                            </Typography>
                            {selected.instructions && (
                                <>
                                    <Typography variant="subtitle1">{LANG.MENU_INSTRUCTIONS}</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                                        {selected.instructions}
                                    </Typography>
                                </>
                            )}
                            {selected.notes && (
                                <>
                                    <Typography variant="subtitle1">{LANG.MENU_NOTES}</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {selected.notes}
                                    </Typography>
                                </>
                            )}
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Box>
    );
}
